
import { useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { CircularProgress } from '@mui/material'
import ModalHeader from './ModalHeader'
import { isEqual, sortBy } from 'lodash'
import useGeoFeatures from '../hooks/useGeoFeatures'
import { FeatureConfigType, GeoFeatureDataType } from '../utils/types'
import { checkCommonNameValidation, getCharacterLimitMessage, removeWhiteSpace, showToastMessage } from '../../../utils/commonFunctions'
import { GeoFeatureSvg } from '../Svgs'
import { POLYGON_TYPES, toggleMinimizeModal } from '../utils/utilities'
import MissionService from '../../../service/MissionService'


const FeatureDetailsModal = () => {
    const { mission_id } = useParams()
    const { sidebarOpen, getGeoFeatures, selectedFeature, setSelectedFeature, setSidebarOpen } = useGeoFeatures()

    const open = useMemo(() => selectedFeature?.openModal === 'details', [selectedFeature?.openModal])
    const previousData = useMemo(() => selectedFeature?.previousData as GeoFeatureDataType, [selectedFeature?.previousData])
    const data = useMemo(() => selectedFeature?.data as FeatureConfigType, [selectedFeature?.data])
    const type = useMemo(() => data?.type, [data?.type])
    const name = useMemo(() => data?.name, [data?.name])
    const description = useMemo(() => data?.description, [data?.description])
    const polygon = useMemo(() => data?.polygon, [data?.polygon])
    const coordinates = useMemo(() => data?.coordinates, [data?.coordinates])
    const mode = useMemo(() => selectedFeature?.mode, [selectedFeature?.mode])
    const uniqueId = useMemo(() => selectedFeature?.uniqueId, [selectedFeature?.uniqueId])
    const loading = useMemo(() => selectedFeature?.loading, [selectedFeature?.loading])
    const reset = useMemo(() => selectedFeature?.resetConfig, [selectedFeature])

    const normalize = useCallback((arr) => sortBy(arr.map(sub => sortBy(sub))), []);

    const disabled = useMemo(() => loading || !data?.name || !type ||
        (mode === 'Create' && (POLYGON_TYPES.includes(type) ? !polygon : !coordinates)) ||
        (mode === 'Update' && (isEqual(name, previousData?.name) && isEqual(description, previousData?.description) && (POLYGON_TYPES.includes(type) ? isEqual(normalize(polygon || []), normalize(previousData?.polygon || [])) : isEqual(coordinates, previousData?.coordinates)))), [type, polygon, coordinates, mode, name, previousData, loading, description])

    const getLoadingButtonText = () => {
        switch (mode) {
            case 'Create':
                return 'Creating'
            case 'Update':
                return 'Updating'
            default:
                return 'Create'
        }
    }

    const close = () => {
        setSelectedFeature(null)
        setSidebarOpen(true)
        reset?.()
    }

    const onChangeValue = (key: string, value: string) => {
        if (key === 'name' && !checkCommonNameValidation(value, 26)) return
        else if (key === 'description' && value?.length > 200) {
            showToastMessage('info', getCharacterLimitMessage(200))
            return
        }
        setSelectedFeature({
            ...selectedFeature!,
            data: {
                ...data!,
                [key]: value,
            } as FeatureConfigType
        })
    }

    const submitForm = async () => {
        try {
            setSelectedFeature({
                ...selectedFeature!,
                loading: true,
            })
            if (mode === 'Create') {
                const body = {
                    "areaOfInterests": [{ ...data, type: POLYGON_TYPES.includes(data?.type!) ? 'Geofence' : 'Target Track' }],
                    "name": "Janus5",
                    "type": "Single"
                }
                await MissionService.postFeature(mission_id, body);
                await getGeoFeatures()
                showToastMessage('success', `GeoFeature created successfully`)
            } else {
                const body = {
                    ...data,
                    type: POLYGON_TYPES.includes(data?.type!) ? 'Geofence' : 'Target Track',
                    areaOfInterestManagerId: selectedFeature?.areaOfInterestManagerId
                }
                await MissionService.updateAoiById(uniqueId, body);
                await getGeoFeatures()
                showToastMessage('success', `GeoFeature updated successfully`)
            }
            reset?.()
        } catch (error: any) {
            showToastMessage('error', error?.response?.data?.message || `An error occurred while ${mode === 'Create' ? 'creating' : 'updating'} the GeoFeature. Please try again.`)
            setSelectedFeature({
                ...selectedFeature!,
                loading: false
            })
        }
    }


    return (
        <div id='feature__details__modal'
            className={`feature__modal__container__wrapper feature__details__modal 
            ${open && 'active'} ${sidebarOpen && 'shift__left'}`}>
            <ModalHeader
                open={open ? true : false}
                title='GEOFEATURE'
                icon={<GeoFeatureSvg />}
                onClose={close}
                onMinimize={() => toggleMinimizeModal('feature__details__modal')}
                disabled={loading ? true : false}
            />
            <div className='feature__modal__body__container'>
                <div className='body__container__scroll__section'>
                    <div className='body__form__section'>
                        <div className='form__field__container'>
                            <span className='form__field__label'>Name</span>
                            <div className='form__input_field__container w-70'>
                                <input type='text'
                                    className='form__field__input'
                                    placeholder='Area of Interest'
                                    disabled={loading || !type}
                                    value={name}
                                    onChange={(e) => {
                                        onChangeValue('name', removeWhiteSpace(e.target.value))
                                    }}
                                />
                            </div>
                        </div>
                        <div className='form__field__container'>
                            <span className='form__field__label'>Description</span>
                            <div className='form__input_field__container w-70'>
                                <textarea
                                    className='form__field__textarea'
                                    placeholder='Description'
                                    disabled={loading || !type}
                                    rows={4}
                                    value={description}
                                    onChange={(e) => {
                                        onChangeValue('description', removeWhiteSpace(e.target.value))
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className='feature__modal__footer__container'>
                <button className='footer__cancel__button' disabled={loading} onClick={close}>Cancel</button>
                <button className='footer__confirm__button'
                    disabled={disabled}
                    onClick={submitForm}
                >
                    {loading ? getLoadingButtonText() : mode} {loading && <CircularProgress size={14} color='inherit' />}
                </button>
            </div>
        </div>
    )
}

export default FeatureDetailsModal